import api from './api';

const TRACKING_SESSION_KEY = 'cs_tracking_session';

export interface GeoInfo {
  city?: string | null;
  region?: string | null;
  country?: string | null;
  lat?: number | null;
  lon?: number | null;
  accuracy?: number | null;
}

export interface PageVisitPayload {
  page: string;
  source?: string | null;
  referrer?: string | null;
  session_id?: string;
  user_agent?: string;
  ip?: string;
  geo?: GeoInfo;
}

export const getOrCreateTrackingSessionId = (): string => {
  const existing = localStorage.getItem(TRACKING_SESSION_KEY);
  if (existing) {
    try {
      localStorage.setItem('tracking_session_id', existing);
    } catch {
      /* ignore storage errors */
    }
    return existing;
  }

  const generated = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  localStorage.setItem(TRACKING_SESSION_KEY, generated);
  try {
    localStorage.setItem('tracking_session_id', generated);
  } catch {
    /* ignore storage errors */
  }
  return generated;
};

const GEO_CACHE_KEY = 'cs_geo_info';
const GEO_CACHE_TTL_MS = 1000 * 60 * 30; // 30 minutes

const readCachedGeo = (): GeoInfo | null => {
  try {
    const raw = localStorage.getItem(GEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp) return null;
    if (Date.now() - parsed.timestamp > GEO_CACHE_TTL_MS) return null;
    return parsed.geo ?? null;
  } catch {
    return null;
  }
};

const writeCachedGeo = (geo: GeoInfo) => {
  try {
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ geo, timestamp: Date.now() }));
  } catch {
    /* ignore storage errors */
  }
};

export const getBrowserGeo = async (): Promise<GeoInfo | undefined> => {
  if (typeof window === 'undefined' || !('geolocation' in navigator)) return undefined;

  const cached = readCachedGeo();
  if (cached) return cached;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const geo: GeoInfo = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        };
        writeCachedGeo(geo);
        resolve(geo);
      },
      () => resolve(undefined),
      { enableHighAccuracy: false, timeout: 2000, maximumAge: GEO_CACHE_TTL_MS },
    );
  });
};

export interface PageExitPayload {
  page: string;
  session_id?: string;
  campaign_source?: string;
  time_on_page_seconds: number;
  scroll_depth_percent: number;
  last_section_reached: string;
  sections_viewed: string[];
  exit_trigger: 'beforeunload' | 'visibilitychange' | 'navigation';
}

export const trackingAPI = {
  trackPageVisit: (payload: PageVisitPayload) =>
    api.post('/tracking/visit', payload),
  trackPageExit: (payload: PageExitPayload) =>
    api.post('/tracking/page-exit', payload),
};

export default trackingAPI;
